// Publicacoes recorrentes: lista com regra, proxima execucao e status; pausar,
// retomar, editar e excluir. A criacao fica no Criar Posts (modo Recorrente).

import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { CalendarClock, Pause, Pencil, Play, Plus, Repeat, Trash2 } from "lucide-react";
import { describeRecurrence } from "@lucralink/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PageHeader } from "@/components/PageHeader";
import { PlanGate } from "@/components/billing/PlanGate";
import { RecurringEditForm } from "@/components/posts/RecurringEditForm";
import {
  useDeleteRecurringPost,
  useRecurringPosts,
  useSetRecurringStatus,
  type RecurringPost,
} from "@/lib/hooks/useRecurringPosts";

const STATUS_LABEL: Record<RecurringPost["status"], string> = { active: "Ativa", paused: "Pausada", finished: "Encerrada" };
const STATUS_VARIANT: Record<RecurringPost["status"], "success" | "warning" | "muted"> = {
  active: "success",
  paused: "warning",
  finished: "muted",
};

const PAUSE_REASON: Record<string, string> = {
  plan_required: "Pausada porque o plano PRO venceu.",
  destinations_unavailable: "Pausada porque os destinos não estão mais conectados.",
};

const SKIP_REASON: Record<string, string> = {
  pending_exists: "a anterior ainda estava na fila",
  window_closed: "fora da janela de envio",
  missed: "o horário passou com o envio parado",
  ended: "a recorrência terminou",
  rule_invalid: "regra inválida",
};

export function formatRunAt(iso: string | null): string {
  if (!iso) return "sem próxima execução";
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
}

interface RowProps {
  readonly post: RecurringPost;
  readonly onMessage: (message: string, isError?: boolean) => void;
}

function RowActions({ post, onMessage, onEdit }: RowProps & { onEdit: () => void }): JSX.Element {
  const setStatus = useSetRecurringStatus();
  const remove = useDeleteRecurringPost();
  const [confirming, setConfirming] = useState(false);
  const run = async (action: () => Promise<unknown>, ok: string): Promise<void> => {
    try {
      await action();
      onMessage(ok);
    } catch (e) {
      onMessage(e instanceof Error ? e.message : "Não foi possível concluir a ação.", true);
    }
  };
  const busy = setStatus.isPending || remove.isPending;
  return (
    <div className="flex flex-wrap gap-2">
      {post.status === "active" ? (
        <Button size="sm" variant="secondary" disabled={busy} onClick={() => void run(() => setStatus.mutateAsync({ post, status: "paused" }), "Publicação pausada.")}>
          <Pause size={13} /> Pausar
        </Button>
      ) : (
        <Button size="sm" variant="secondary" disabled={busy} onClick={() => void run(() => setStatus.mutateAsync({ post, status: "active" }), "Publicação retomada.")}>
          <Play size={13} /> Retomar
        </Button>
      )}
      <Button size="sm" variant="outline" onClick={onEdit}>
        <Pencil size={13} /> Editar
      </Button>
      {confirming ? (
        <Button size="sm" variant="destructive" disabled={busy} onClick={() => void run(() => remove.mutateAsync(post.id), "Publicação excluída.")}>
          Confirmar exclusão
        </Button>
      ) : (
        <Button size="sm" variant="ghost" aria-label="Excluir publicação" onClick={() => setConfirming(true)}>
          <Trash2 size={13} /> Excluir
        </Button>
      )}
    </div>
  );
}

function RecurringRow({ post, onMessage }: RowProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  return (
    <Card variant="raised">
      <CardContent className="space-y-3 pt-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <Repeat size={14} className="text-primary" />
              {post.rule ? describeRecurrence(post.rule) : "Regra inválida"}
            </p>
            <p className="line-clamp-2 text-sm text-muted-foreground">{post.bodyText}</p>
          </div>
          <Badge variant={STATUS_VARIANT[post.status]}>{STATUS_LABEL[post.status]}</Badge>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <CalendarClock size={12} /> Próxima: {post.status === "active" ? formatRunAt(post.nextRunAt) : "-"}
          </span>
          <span>{post.deliveries.length} destino(s)</span>
          <span>{post.occurrencesCount} publicação(ões) feitas</span>
          {post.endsAt && <span>Termina em {formatRunAt(post.endsAt)}</span>}
        </div>
        {post.pauseReason && <p className="text-xs text-amber-500">{PAUSE_REASON[post.pauseReason] ?? "Pausada."}</p>}
        {post.lastSkipReason && (
          <p className="text-xs text-muted-foreground">
            Última ocorrência pulada ({formatRunAt(post.lastSkipAt)}): {SKIP_REASON[post.lastSkipReason] ?? "motivo não informado"}
          </p>
        )}
        <RowActions post={post} onMessage={onMessage} onEdit={() => setEditing((v) => !v)} />
        {editing && (
          <RecurringEditForm
            post={post}
            onCancel={() => setEditing(false)}
            onDone={(message) => {
              setEditing(false);
              onMessage(message);
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}

function RecorrentesPageContent(): JSX.Element {
  const { data: posts = [], isLoading, isError } = useRecurringPosts();
  const [message, setMessage] = useState<{ text: string; isError: boolean } | null>(null);
  return (
    <div className="max-w-4xl space-y-6">
      <PageHeader title="Publicações recorrentes" subtitle="Posts que se repetem sozinhos por dia da semana e horário, ou a cada X horas." />
      <div className="flex justify-end">
        <Button asChild size="sm">
          <Link to="/app/criar-posts">
            <Plus size={14} /> Nova publicação recorrente
          </Link>
        </Button>
      </div>
      {message && (
        <p role={message.isError ? "alert" : "status"} className={message.isError ? "text-sm text-destructive" : "text-sm text-primary"}>
          {message.text}
        </p>
      )}
      {isError && <p role="alert" className="text-sm text-destructive">Não foi possível carregar as publicações recorrentes.</p>}
      {isLoading && <p className="text-sm text-muted-foreground">Carregando...</p>}
      {!isLoading && !isError && posts.length === 0 && (
        <Card variant="ghost">
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Nenhuma publicação recorrente ainda. Crie uma em Criar Posts escolhendo &quot;Recorrente&quot;.
          </CardContent>
        </Card>
      )}
      <div className="space-y-4">
        {posts.map((post) => (
          <RecurringRow key={post.id} post={post} onMessage={(text, isError = false) => setMessage({ text, isError })} />
        ))}
      </div>
    </div>
  );
}

/** Recorrentes: disparo/agendamento é PRO (gate central; enforcement no banco). */
export function RecorrentesPage(): JSX.Element {
  return (
    <PlanGate feature="bulkSend" title="Publicações recorrentes" subtitle="Posts que se repetem sozinhos por dia da semana e horário, ou a cada X horas.">
      <RecorrentesPageContent />
    </PlanGate>
  );
}
